import Store from 'repatch';
import State from '../core/State';
import RootDomain from '../entity/RootDomain';
import SpeculationRule from '../entity/SpeculationRule';
import ApiErrorHandler from '../service/ApiErrorHandler';
import JavaScriptCodeWebApi from '../service/persistent/JavaScriptCodeWebApi';
import { finishFetchingRootDomains, setRootDomains, startFetchingRootDomains } from '../thunk/rootDomains';
import {
  finishFetchingSiteJsPermission,
  setSiteJsPermission,
  startFetchingSiteJsPermission,
} from '../thunk/siteJsPermission';
import { finishFetchingSiteScript, setSiteScript, startFetchingSiteScript } from '../thunk/siteScript';
import { finishFetchingSpeculationRule, setSpeculationRule, startFetchingSpeculationRule } from '../thunk/speculationRule';
import { AxiosError } from 'axios';

class JavaScriptCodeUsecase {
  private store: Store<State>;
  private javaScriptCodeWebApi: JavaScriptCodeWebApi;
  private apiErrorHandler: ApiErrorHandler;

  constructor({
    store,
    javaScriptCodeWebApi,
    apiErrorHandler,
  }: {
    store: Store<State>;
    javaScriptCodeWebApi: JavaScriptCodeWebApi;
    apiErrorHandler: ApiErrorHandler;
  }) {
    this.store = store;
    this.javaScriptCodeWebApi = javaScriptCodeWebApi;
    this.apiErrorHandler = apiErrorHandler;
  }

  public getSiteScript = async (): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;

    this.store.dispatch(startFetchingSiteScript());

    try {
      const siteScript = await this.javaScriptCodeWebApi.getSiteScript(organizationId);

      if (!siteScript) {
        this.store.dispatch(finishFetchingSiteScript());
        return;
      }

      this.store.dispatch(setSiteScript(siteScript));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingSiteScript());
    }
  };

  public getRootDomains = async (): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;

    this.store.dispatch(startFetchingRootDomains());

    try {
      const rootDomains = await this.javaScriptCodeWebApi.getRootDomains(organizationId);

      if (!rootDomains) {
        this.store.dispatch(finishFetchingRootDomains());
        return;
      }

      this.store.dispatch(setRootDomains(rootDomains));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingRootDomains());
    }
  };

  public getSiteJsPermission = async (): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;

    this.store.dispatch(startFetchingSiteJsPermission());

    try {
      const siteJsPermission = await this.javaScriptCodeWebApi.getSiteJsPermission(organizationId);

      if (siteJsPermission === undefined || siteJsPermission === null) {
        this.store.dispatch(finishFetchingSiteJsPermission());
        return;
      }

      this.store.dispatch(setSiteJsPermission(siteJsPermission));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingSiteJsPermission());
    }
  };

  public postRootDomains = async (name: string): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;

    this.store.dispatch(startFetchingRootDomains());

    try {
      await this.javaScriptCodeWebApi.postRootDomain(organizationId, name);
      await this.getRootDomains();
    } catch (error) {
      throw new Error();
    } finally {
      this.store.dispatch(finishFetchingRootDomains());
    }
  };

  public putRootDomains = async (name: string, rootDomain: RootDomain): Promise<void> => {
    this.store.dispatch(startFetchingRootDomains());

    try {
      await this.javaScriptCodeWebApi.putRootDomain(name, rootDomain);
      await this.getRootDomains();
    } catch (error) {
      throw new Error();
    } finally {
      this.store.dispatch(finishFetchingRootDomains());
    }
  };

  public deleteRootDomains = async (rootDomain: RootDomain): Promise<void> => {
    this.store.dispatch(startFetchingRootDomains());

    try {
      await this.javaScriptCodeWebApi.deleteRootDomain(rootDomain);
      await this.getRootDomains();
    } catch (error) {
      throw new Error();
    } finally {
      this.store.dispatch(finishFetchingRootDomains());
    }
  };

  public putSiteScript = async (isEmpty: boolean): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;

    this.store.dispatch(startFetchingSiteScript());

    try {
      await this.javaScriptCodeWebApi.putSiteScript(organizationId, isEmpty);
      await this.getSiteScript();
    } catch (error) {
      throw new Error();
    } finally {
      this.store.dispatch(finishFetchingSiteScript());
    }
  };

  public getSpeculationRules = async (): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;

    this.store.dispatch(startFetchingSpeculationRule());

    try {
      const speculationRule = await this.javaScriptCodeWebApi.getSpeculationRule(organizationId);
      
      if (!speculationRule) {
        this.store.dispatch(finishFetchingSpeculationRule());
        return;
      }

      this.store.dispatch(setSpeculationRule(speculationRule));
    } catch (error) {
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingSpeculationRule());
    }
  };

  public putSpeculationRule = async (speculationRule: SpeculationRule): Promise<void> => {
    const state = this.store.getState();
    const organizationId = state.lastAccessedOrganization.item!.id;

    this.store.dispatch(startFetchingSpeculationRule());

    try {
      const updatedSpeculationRule = await this.javaScriptCodeWebApi.putSpeculationRule(organizationId, speculationRule);
      this.store.dispatch(setSpeculationRule(updatedSpeculationRule));
    } catch (error) {
      if (error instanceof AxiosError) {
        if (error.response?.status === 400 && error.response?.data?.errors instanceof Array) {
          // 入力エラーになり得るパターンは正規表現エラーのみで、UI上で防いでいるので通常起きないはずだが、念のため
          throw new Error(`設定の保存に失敗しました: ${error.response?.data.errors[0]}`);
        }
        if (error.response?.status === 403) {
          // write権限が無い場合のエラー
          throw new Error('設定を保存する権限がありません');
        }
      }
      this.apiErrorHandler.handle(error);
    } finally {
      this.store.dispatch(finishFetchingSpeculationRule());
    }
  };
}

export default JavaScriptCodeUsecase;
